import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import API_BASE_URL from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";
import axios from "axios";
import { FaCheck, FaTimes, FaEdit, FaUserTie, FaBuilding, FaArrowLeft, FaHashtag, FaUsers, FaBullhorn, FaCalendarAlt, FaEnvelope, FaExclamationCircle, FaSave } from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import SearchableDropdown from "../../../componenets/SearchableDropdown";

const ReadMore = ({ text, wordLimit = 50 }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!text) return null;

    const words = text.trim().split(/\s+/);
    if (words.length <= wordLimit) {
        return <p className="text-gray-700 leading-relaxed whitespace-pre-line">{text}</p>;
    }

    const visibleText = isExpanded ? text : words.slice(0, wordLimit).join(" ") + "...";

    return (
        <div>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {visibleText}
                <span
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="text-[#B7202E] font-medium cursor-pointer ml-2 hover:underline"
                >
                    {isExpanded ? "Show Less" : "Read More"}
                </span>
            </p>
        </div>
    );
};



function ProjectDetailsContainer() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { activeRole, isAuthenticated, user } = useAuth();


    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [approved, setApproved] = useState(false);
    const [rejected, setRejected] = useState(false);
    const [modifications, setModifications] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState(null);
    const [comment, setComment] = useState("");
    const [selectedGuide, setSelectedGuide] = useState("");
    const [showGuideDropdown, setShowGuideDropdown] = useState(false);
    const [showCoGuideDropdown, setShowCoGuideDropdown] = useState(false);
    const [selectedCoGuide, setSelectedCoGuide] = useState("");
    const [announcements, setAnnouncements] = useState([]);
    const [showThrustAreaDropdown, setShowThrustAreaDropdown] = useState(false);
    const [selectedThrustArea, setSelectedThrustArea] = useState("");
    const [thrustAreas, setThrustAreas] = useState([]);
    const [userDepartment, setUserDepartment] = useState("");
    const [loadingThrustAreas, setLoadingThrustAreas] = useState(false);

    // Edit Mode State
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [guides, setGuides] = useState([]);
    const [domains, setDomains] = useState([]);
    const [industryExperts, setIndustryExperts] = useState([]);
    const [loadingDropdowns, setLoadingDropdowns] = useState(false);

    const [displayMembers, setDisplayMembers] = useState([]);





    useEffect(() => {
        const fetchMemberDetails = async () => {
            const members = project?.teamMembers || [];
            if (members.length === 0) {
                setDisplayMembers([]);
                return;
            }

            const details = [];
            for (const roll of members) {
                try {
                    const response = await axios.get(`${API_BASE_URL}/api/students/${roll}`);
                    if (response.status === 200) {
                        details.push(response.data);
                    }
                } catch (error) {
                    console.error("Error fetching student details:", error);
                    details.push({ rollNo: roll, name: "Unknown Student" }); // Fallback
                }
            }
            setDisplayMembers(details);
        };

        if (project) {
            fetchMemberDetails();
        }
    }, [project]);



    const loadProjectDetails = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${API_BASE_URL}/api/projects/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${user.token}`
                    }
                }
            );

            if (response.status === 200) {
                setProject(response.data);
            } else {
                throw new Error("Failed to fetch project details");
            }
        } catch (error) {
            console.error("Error fetching project details:", error);
            toast.error("Error loading project details");
            if (error.response?.status === 401) {
                navigate("/");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isAuthenticated) {
            toast.error("You must be logged in to view project details");
            navigate("/");
            return;
        }

        loadProjectDetails();
        AOS.init({ duration: 800 });
    }, [id, isAuthenticated, navigate]);

    useEffect(() => {
        const fetchAnnouncements = async () => {
            try {
                const response = await axios.get(
                    `${API_BASE_URL}/api/projects/${id}/announcements`
                );

                if (response.status === 200) {
                    setAnnouncements(response.data);
                }
            } catch (error) {
                console.error("Error fetching announcements:", error);
            }
        };

        if (id && isAuthenticated) {
            fetchAnnouncements();
        }
    }, [id, isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated && (activeRole === "pc" || activeRole === "thrustarea") && user) {
            const fetchCoordinatorDepartment = async () => {
                try {
                    const facultyId = user.facultyId;
                    if (!facultyId) {
                        throw new Error("Faculty ID not found in user data");
                    }

                    const response = await axios.get(
                        `${API_BASE_URL}/api/project-coordinators/${facultyId}`
                    );

                    if (response.status === 200 && response.data) {
                        const department = response.data.department;
                        setUserDepartment(department);
                        fetchThrustAreas(department);
                    }
                } catch (error) {
                    console.error("Error fetching coordinator department:", error);
                    if (user && user.department) {
                        setUserDepartment(user.department);
                        fetchThrustAreas(user.department);
                    }
                }
            };

            fetchCoordinatorDepartment();
        }
    }, [isAuthenticated, activeRole, user]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

    const fetchThrustAreas = async (department) => {
        if (!department) return;

        setLoadingThrustAreas(true);

        const deptMap = {
            "Information Technology": "Artificial Intelligence and Data Science",
            "Artificial Intelligence and Data Science": "Information Technology",
            "Electronics and Telecommunication Engineering": "Computer and Communication Engineering",
            "Computer and Communication Engineering": "Electronics and Telecommunication Engineering",
            "Mechanical Engineering": "Robotics and Artificial Intelligence",
            "Robotics and Artificial Intelligence": "Mechanical Engineering"
        };

        let departmentsToFetch = [department];
        if (deptMap[department]) {
            departmentsToFetch.push(deptMap[department]);
        }

        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/thrust-areas`,
                {
                    params: {
                        department: departmentsToFetch.join(',')
                    },
                    headers: {
                        Authorization: `Bearer ${user.token}`
                    }
                }
            );

            if (response.status === 200) {
                setThrustAreas(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching thrust areas:", error);
            // toast.error("Error loading thrust areas");
        } finally {
            setLoadingThrustAreas(false);
        }
    };

    const refreshAnnouncements = async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/projects/${id}/announcements`,
                {
                    headers: {
                        Authorization: `Bearer ${user.token}`
                    }
                }
            );

            if (response.status === 200) {
                setAnnouncements(response.data);
            }
        } catch (error) {
            console.error("Error refreshing announcements:", error);
        }
    };

    const fetchDropdownData = async () => {
        if (loadingDropdowns || guides.length > 0) return;
        setLoadingDropdowns(true);
        try {
            const [guidesRes, domainsRes, expertsRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/guides?limit=1000`),
                axios.get(`${API_BASE_URL}/api/domains`),
                axios.get(`${API_BASE_URL}/api/experts?limit=1000`)
            ], {
                headers: {
                    "Authorization": `Bearer ${user?.token}`,
                },
            });

            setGuides(guidesRes.data.guides || []);
            setDomains(domainsRes.data);
            setIndustryExperts(expertsRes.data.experts || []);
        } catch (error) {
            console.error("Error fetching dropdown data:", error);
            toast.error("Error loading form data");
        } finally {
            setLoadingDropdowns(false);
        }
    };

    const handleEditClick = () => {
        setEditData(JSON.parse(JSON.stringify(project))); // Deep copy
        setIsEditing(true);
        fetchDropdownData();
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditData({});
    };

    const handleEditChange = (e, field, subfield = null, subsubfield = null) => {
        const value = e.target.value;
        setEditData(prev => {
            const newData = { ...prev };
            if (subsubfield) {
                if (!newData[field]) newData[field] = {};
                if (!newData[field][subfield]) newData[field][subfield] = {};
                newData[field][subfield][subsubfield] = value;
            } else if (subfield) {
                if (!newData[field]) newData[field] = {};
                newData[field][subfield] = value;
            } else {
                newData[field] = value;
            }
            return newData;
        });
    };

    const handleCategoryChange = (key, value) => {
        setEditData(prev => ({
            ...prev,
            category: {
                ...prev.category,
                [key]: value
            }
        }));
    };

    const handleDomainChange = (e) => {
        const selectedDomain = domains.find(d => d.name === e.target.value);
        if (!selectedDomain) return;

        setEditData(prev => ({
            ...prev,
            domain: {
                preference1: {
                    name: selectedDomain.name,
                    department: selectedDomain.department,
                    thrustArea: selectedDomain.thrustArea
                }
            }
        }));
    };



    const handleEditIndustryExpertSelect = (e) => {
        const expertId = e.target.value;
        const selectedExpert = industryExperts.find(ex => ex._id === expertId);

        if (selectedExpert) {
            setEditData(prev => ({
                ...prev,
                industryExpert: {
                    name: selectedExpert.name,
                    email: selectedExpert.email,
                    company: selectedExpert.industry || selectedExpert.company,
                    designation: selectedExpert.designation
                }
            }));
        }
    };

    const countWords = (str) => {
        if (!str) return 0;
        return str.trim().split(/\s+/).filter(word => word.length > 0).length;
    };

    const validateEditForm = () => {
        // Project Title Validation
        if (!editData.projectTitle || !editData.projectTitle.trim()) {
            toast.error("Please enter project title");
            return false;
        }
        if (countWords(editData.projectTitle) > 20) {
            toast.error("Project Title should not be more than 20 words");
            return false;
        }

        // Objectives Validation
        if (!editData.objectives || !editData.objectives.trim()) {
            toast.error("Please enter objectives");
            return false;
        }
        const objectivesWordCount = countWords(editData.objectives);
        if (objectivesWordCount < 10 || objectivesWordCount > 500) {
            toast.error("Objectives should be between 10 and 500 words");
            return false;
        }

        // Idea & Inspiration Validation
        if (!editData.ideaInspiration || !editData.ideaInspiration.trim()) {
            toast.error("Please provide idea inspiration");
            return false;
        }
        const ideaWordCount = countWords(editData.ideaInspiration);
        if (ideaWordCount < 10 || ideaWordCount > 500) {
            toast.error("Idea and Inspiration should be between 10 and 500 words");
            return false;
        }

        // Literature Survey Validation
        if (!editData.literatureSurvey || !editData.literatureSurvey.trim()) {
            toast.error("Please provide a valid URL for literature survey");
            return false;
        }
        const litSurveyLink = editData.literatureSurvey.trim();
        if (!litSurveyLink.startsWith("https://drive.google.com/drive/folders") || !litSurveyLink.endsWith("?usp=sharing")) {
            toast.error("Enter a valid Literature Survey link and make sure you made it accessible to Somaiya Vidyavihar University or anyone with link");
            return false;
        }

        // References Validation
        if (!editData.references || !editData.references.trim()) {
            toast.error("Please provide references");
            return false;
        }

        // Objectives Term 1 Validation
        if (!editData.objectivesTerm1 || !editData.objectivesTerm1.trim()) {
            toast.error("Please provide objectives for 6th sem");
            return false;
        }
        const objTerm1WordCount = countWords(editData.objectivesTerm1);
        if (objTerm1WordCount < 10 || objTerm1WordCount > 500) {
            toast.error("Objectives for 6th sem should be between 10 and 500 words");
            return false;
        }

        // Objectives Term 2 Validation
        if (!editData.objectivesTerm2 || !editData.objectivesTerm2.trim()) {
            toast.error("Please provide objectives for 7th sem");
            return false;
        }
        const objTerm2WordCount = countWords(editData.objectivesTerm2);
        if (objTerm2WordCount < 10 || objTerm2WordCount > 500) {
            toast.error("Objectives for 7th sem should be between 10 and 500 words");
            return false;
        }

        return true;
    };

    const handleSaveEdit = async () => {
        try {
            if (!validateEditForm()) {
                return;
            }

            const response = await axios.put(
                `${API_BASE_URL}/api/projects/${id}/update`,
                editData, {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            }
            );

            if (response.status === 200) {
                toast.success("Project updated successfully");
                setProject(response.data.data);
                setIsEditing(false);
                await refreshAnnouncements();

                // Refresh project details instead of navigating
                loadProjectDetails();
            }
        } catch (error) {
            console.error("Error updating project:", error);
            toast.error(error.response?.data?.message || "Error updating project");
        }
    };

    const handleNavigationClick = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const yOffset = -100; // Adjust for sticky header
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    };

    const handleAccept = () => {
        setApproved(true);
        setModifications(false);
        setRejected(false);
    };

    const handleReject = () => {
        setRejected(true);
        setModifications(false);
        setApproved(false);
    };

    const handleModifications = () => {
        setModifications(true);
        setApproved(false);
        setRejected(false);
    };

    const handleSend = async () => {
        try {
            let action;
            if (approved) action = "accepted";
            if (rejected) action = "rejected";
            if (modifications) action = "modifications";

            if (!action) {
                toast.error("Invalid action selected");
                return;
            }

            const response = await axios.put(
                `${API_BASE_URL}/api/projects/${id}/status`,
                {
                    status: action,
                    message: comment || "",
                    action: action,
                }
            );

            if (response.status === 200) {
                toast.success(`Project ${action} successfully`);
                await refreshAnnouncements();

                // Refresh project details instead of navigating
                loadProjectDetails();
                setComment(""); // Clear comment
                setApproved(false);
                setRejected(false);
                setModifications(false);
            } else {
                toast.error(response.data.message || "Error updating project status");
            }
        } catch (error) {
            console.error("Error updating project status:", error);
            toast.error("Error updating project status");
        }
    };

    const handleGuideAssignment = async (guideEmail) => {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/projects/${id}/guide`,
                { guideEmail }
            );

            if (response.status === 200) {
                setConfirmationMessage("Guide assigned successfully");
                return true;
            }
            throw new Error("Failed to assign guide");
        } catch (error) {
            console.error("Error assigning guide:", error);
            toast.error("Error assigning guide");
            return false;
        }
    };

    const handleCoGuideAssignment = async (coGuideEmail) => {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/projects/${id}/coguide`,
                { coGuideEmail }
            );

            if (response.status === 200) {
                setConfirmationMessage("Co-Guide assigned successfully");
                return true;
            }
            throw new Error("Failed to assign co-guide");
        } catch (error) {
            console.error("Error assigning co-guide:", error);
            toast.error("Error assigning co-guide");
            return false;
        }
    };

    const handleCancel = () => {
        setApproved(false);
        setRejected(false);
        setModifications(false);
    };

    const handleAssignGuide = () => {
        fetchDropdownData();
        setShowGuideDropdown(true);
    };
    const handleAssignCoGuide = () => {
        fetchDropdownData();
        setShowCoGuideDropdown(true);
    };

    const handleGuideSelect = async (e) => {
        const selectedValue = e.target.value;
        if (!selectedValue) return;

        try {
            const success = await handleGuideAssignment(selectedValue);
            if (success) {
                await refreshAnnouncements();
                loadProjectDetails(); // Refresh data
            }
        } catch (error) {
            toast.error("Error assigning guide");
        }
        setShowGuideDropdown(false);
    };

    const handleCoGuideSelect = async (e) => {
        const selectedValue = e.target.value;
        if (!selectedValue) return;

        try {
            const success = await handleCoGuideAssignment(selectedValue);
            if (success) {
                await refreshAnnouncements();
                loadProjectDetails(); // Refresh data
            }
        } catch (error) {
            toast.error("Error assigning co-guide");
        }
        setShowCoGuideDropdown(false);
    };

    const handleAssignThrustArea = () => setShowThrustAreaDropdown(true);

    const handleThrustAreaSelect = async (e) => {
        const selectedValue = e.target.value;
        if (!selectedValue) return;

        setSelectedThrustArea(selectedValue);

        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/projects/${id}/thrust-area`,
                { thrustArea: selectedValue },
                {
                    headers: {
                        Authorization: `Bearer ${user.token}`
                    }
                }
            );

            if (response.status === 200) {
                toast.success("Thrust area assigned successfully");
                refreshAnnouncements();
                loadProjectDetails(); // Refresh data
            } else {
                throw new Error("Failed to assign thrust area");
            }
        } catch (error) {
            console.error("Error assigning thrust area:", error);
            toast.error("Error assigning thrust area");
        } finally {
            setShowThrustAreaDropdown(false);
        }
    };

    const getEmail = (preference) => {
        if (!preference) return "";
        if (typeof preference === "string") return preference;
        return preference.email || "";
    };

    const getNavigationItems = () => [
        { label: "Project Title", id: "project-title" },
        { label: "Category", id: "category" },
        { label: "Objectives", id: "objectives" },
        { label: "Idea & Inspiration", id: "idea-inspiration" },
        { label: "Literature Survey", id: "literature-survey" },
        { label: "References", id: "references" },
        { label: "Team Members", id: "team-members" },
        { label: "Guide Preferences", id: "guide" },
        { label: "Co-Guide Preferences", id: "co-guides" },
        { label: "Industry Expert", id: "industry-expert" },
        { label: "Objectives for 6th sem", id: "objectives1" },
        { label: "Objectives for 7th sem", id: "objectives2" },
        { label: "Thrust Area", id: "thrust-area" },
    ];

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
                <div className="w-12 h-12 border-4 border-gray-200 border-t-[#B7202E] rounded-full animate-spin mb-4"></div>
                <p className="text-gray-500 font-medium">Loading project details...</p>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 text-gray-500">
                <FaExclamationCircle className="text-4xl mb-4 text-[#B7202E]" />
                <p className="text-lg font-medium">Project not found</p>
                <button
                    onClick={() => navigate(-1)}
                    className="mt-4 text-[#B7202E] hover:underline flex items-center gap-2"
                >
                    <FaArrowLeft /> Go Back
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen pb-12 font-sans relative">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 z-20 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="hover:bg-gray-200 p-2 rounded-full transition-colors text-gray-600"
                        >
                            <FaArrowLeft />
                        </button>
                        <h1 className="text-xl hidden md:block font-bold text-gray-800 truncate max-w-md">
                            Project Details: {project.projectTitle}
                        </h1>
                        {activeRole === "student" && ["pending", "rejected", "needs-modifications"].includes(project.status) && !isEditing && (
                            <button
                                onClick={handleEditClick}
                                className="ml-4 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-bold hover:bg-blue-700 transition-colors flex items-center gap-2"
                            >
                                <FaEdit /> Edit
                            </button>
                        )}
                        {isEditing && (
                            <div className="flex gap-2 ml-4">
                                <button
                                    onClick={handleCancelEdit}
                                    className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-bold hover:bg-gray-300 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveEdit}
                                    className="bg-[#B7202E] text-white px-3 py-1.5 rounded-lg text-sm font-bold hover:bg-[#961925] transition-colors flex items-center gap-2"
                                >
                                    <FaSave /> Save
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide border ${project.status === 'approved' ? 'bg-green-100 text-green-700 border-green-200' : project.status === 'rejected' ? 'bg-red-100 text-red-700 border-red-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}`}>
                            {project.status || "Pending"}
                        </span>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Navigation Sidebar (hidden on mobile) */}
                    <div className="hidden lg:block w-64 flex-shrink-0">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 sticky top-24">
                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 pl-1">
                                Quick Navigation
                            </h3>
                            <ul className="space-y-1">
                                {getNavigationItems().map((item, index) => (
                                    <li
                                        key={index}
                                        onClick={() => handleNavigationClick(item.id)}
                                        className="text-sm font-medium text-gray-600 hover:text-[#B7202E] hover:bg-[#B7202E]/5 px-3 py-2 rounded-lg cursor-pointer transition-all flex items-center gap-2 group"
                                    >
                                        <div className="w-1.5 h-1.5 rounded-full bg-gray-300 group-hover:bg-[#B7202E] transition-colors"></div>
                                        {item.label}
                                    </li>
                                ))}
                            </ul>

                            {/* Status Actions */}
                            {((activeRole === "thrustarea") && project.thrustArea && project.status === "pending") && (
                                <div className="mt-8 pt-6 border-t border-gray-100">
                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 pl-1">
                                        Actions
                                    </h3>
                                    <div className="space-y-2">
                                        <button onClick={handleAccept} className="w-full flex items-center gap-2 justify-center bg-green-100 text-green-700 px-4 py-2 rounded-lg text-sm font-bold hover:bg-green-200 transition-colors">
                                            <FaCheck className="text-xs" /> Approve
                                        </button>
                                        <button onClick={handleReject} className="w-full flex items-center gap-2 justify-center bg-red-100 text-red-700 px-4 py-2 rounded-lg text-sm font-bold hover:bg-red-200 transition-colors">
                                            <FaTimes className="text-xs" /> Reject
                                        </button>
                                        <button onClick={handleModifications} className="w-full flex items-center gap-2 justify-center bg-blue-100 text-blue-700 px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-200 transition-colors">
                                            <FaEdit className="text-xs" /> Modify
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 space-y-6">

                        {/* Project Info Section */}
                        <div id="project-title" className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100" >
                            <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-3 flex items-center gap-2">
                                <FaHashtag /> Project Title
                            </h3>
                            <p className="text-xl md:text-2xl font-bold text-gray-800 leading-tight">
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={editData.projectTitle || ""}
                                        onChange={(e) => handleEditChange(e, "projectTitle")}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B7202E] outline-none"
                                        placeholder="Enter Project Title"
                                    />
                                ) : (
                                    project.projectTitle
                                )}
                            </p>
                        </div>

                        <div id="category" className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" >
                            <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-3 flex items-center gap-2">
                                <FaBuilding /> Category
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {isEditing ? (
                                    <div className="w-full space-y-4">
                                        {/* Type */}
                                        <div className="flex flex-wrap gap-3">
                                            {["departmental", "interdisciplinary"].map((key) => {
                                                const isChecked = editData.category?.[key];
                                                return (
                                                    <div
                                                        key={key}
                                                        onClick={() => {
                                                            // Toggle logic: only one true
                                                            if (!isChecked) {
                                                                setEditData(prev => ({
                                                                    ...prev,
                                                                    category: {
                                                                        ...prev.category,
                                                                        departmental: key === "departmental",
                                                                        interdisciplinary: key === "interdisciplinary"
                                                                    }
                                                                }));
                                                            }
                                                        }}
                                                        className={`cursor-pointer px-3 py-1 rounded-full text-sm font-medium border transition-colors flex items-center gap-2 ${isChecked ? 'bg-[#b7202e] text-white border-[#b7202e]' : 'bg-white text-gray-600 border-gray-300'}`}
                                                    >
                                                        {isChecked && <FaCheck className="text-xs" />}
                                                        <span className="capitalize">{key}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {/* Source */}
                                        <div className="flex flex-wrap gap-3">
                                            {["internal", "external"].map((key) => {
                                                const isChecked = editData.category?.[key];
                                                return (
                                                    <div
                                                        key={key}
                                                        onClick={() => {
                                                            if (!isChecked) {
                                                                setEditData(prev => ({
                                                                    ...prev,
                                                                    category: {
                                                                        ...prev.category,
                                                                        internal: key === "internal",
                                                                        external: key === "external"
                                                                    }
                                                                }));
                                                            }
                                                        }}
                                                        className={`cursor-pointer px-3 py-1 rounded-full text-sm font-medium border transition-colors flex items-center gap-2 ${isChecked ? 'bg-[#b7202e] text-white border-[#b7202e]' : 'bg-white text-gray-600 border-gray-300'}`}
                                                    >
                                                        {isChecked && <FaCheck className="text-xs" />}
                                                        <span className="capitalize">{key}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {Object.entries(project.category || {})
                                            .filter(([_, value]) => value)
                                            .map(([key], idx) => (
                                                <span key={idx} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium border border-gray-200">
                                                    {key}
                                                </span>
                                            ))}
                                        {(!project.category || Object.values(project.category).every(v => !v)) && (
                                            <span className="text-gray-400 italic">No category selected</span>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <div id="objectives" className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" >
                            <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-3 flex items-center gap-2">
                                <FaCheck /> Objectives
                            </h3>
                            {isEditing ? (
                                <textarea
                                    value={editData.objectives || ""}
                                    onChange={(e) => handleEditChange(e, "objectives")}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B7202E] outline-none min-h-[100px]"
                                />
                            ) : (
                                <ReadMore text={project.objectives || "No objectives defined"} />
                            )}
                        </div>

                        <div id="idea-inspiration" className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" >
                            <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-3 flex items-center gap-2">
                                <FaBullhorn /> Idea & Inspiration
                            </h3>
                            {isEditing ? (
                                <textarea
                                    value={editData.ideaInspiration || ""}
                                    onChange={(e) => handleEditChange(e, "ideaInspiration")}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B7202E] outline-none min-h-[100px]"
                                />
                            ) : (
                                <ReadMore text={project.ideaInspiration || "No details provided"} />
                            )}
                        </div>

                        <div id="literature-survey" className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" >
                            <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-3 flex items-center gap-2">
                                <FaCheck /> Literature Survey
                            </h3>
                            <p className="text-gray-700 break-words break-all whitespace-pre-wrap max-w-ful">
                                {isEditing ? (
                                    <textarea
                                        value={editData.literatureSurvey || ""}
                                        onChange={(e) => handleEditChange(e, "literatureSurvey")}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B7202E] outline-none min-h-[100px]"
                                    />
                                ) : (
                                    <a href={project.literatureSurvey} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                                        {project.literatureSurvey || "No details provided"}
                                    </a>
                                )}
                            </p>
                        </div>

                        <div id="references" className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" >
                            <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-3 flex items-center gap-2">
                                <FaCheck /> References
                            </h3>
                            <p className="text-gray-700 break-words break-all whitespace-pre-wrap max-w-ful">
                                {isEditing ? (
                                    <textarea
                                        value={editData.references || ""}
                                        onChange={(e) => handleEditChange(e, "references")}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B7202E] outline-none min-h-[100px]"
                                    />
                                ) : (
                                    project.references || "No details provided"
                                )}
                            </p>
                        </div>

                        <div id="team-members" className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" >
                            <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-3 flex items-center gap-2">
                                <FaUsers /> Team Members
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {displayMembers.length > 0 ? displayMembers.map((member, index) => (
                                    <div key={index} className="bg-[#B7202E]/5 text-[#B7202E] px-4 py-2 rounded-lg font-medium border border-[#B7202E]/20 flex flex-col">
                                        <span className="text-sm">{member.name || member.rollNo || member}</span>
                                        {member.name && <span className="text-xs text-[#B7202E]/70">{member.rollNo}</span>}
                                    </div>
                                )) : (
                                    project.teamMembers?.map((member, index) => (
                                        <div key={index} className="bg-[#B7202E]/5 text-[#B7202E] px-4 py-2 rounded-lg font-medium border border-[#B7202E]/20">
                                            {member}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div id="guide" className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" >
                                <div className="flex justify-between items-start mb-4">
                                    <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider flex items-center gap-2">
                                        <FaUserTie /> Guide Preferences
                                    </h3>
                                    {(activeRole === "thrustarea" || activeRole === "pc") && (project.status === "approved" || project.status === "guide-assigned") && (
                                        <button
                                            onClick={handleAssignGuide}
                                            className="text-xs bg-[#B7202E] text-white px-3 py-1.5 rounded hover:bg-[#961925] transition-colors"
                                        >
                                            {project.guide?.assigned?.email ? "Reassign" : "Assign"}
                                        </button>
                                    )}
                                </div>

                                <ul className="space-y-3">
                                    {[1, 2, 3, 4].map(num => {
                                        const email = getEmail(project.guide?.[`preference${num}`]);
                                        return email ? (
                                            <li key={num} className="flex items-center gap-3 text-sm text-gray-700 p-2 rounded hover:bg-gray-50">
                                                <span className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">{num}</span>
                                                {email}
                                            </li>
                                        ) : null;
                                    })}
                                    {project.guide?.assigned?.email && (
                                        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                            <div className="text-xs font-bold text-green-700 uppercase mb-1">Assigned Guide</div>
                                            <div className="font-medium text-green-900">{project.guide.assigned.email}</div>
                                            {project.guide.assigned.assignedDate && (
                                                <div className="text-xs text-green-600 mt-1">
                                                    Assigned on: {new Date(project.guide.assigned.assignedDate).toLocaleDateString("en-IN", {
                                                        day: 'numeric', month: 'numeric', year: 'numeric'
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </ul>

                                {showGuideDropdown && !isEditing && (
                                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 animate-fade-in">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {project.guide?.assigned?.email ? "Reassign Guide" : "Select Guide"}
                                        </label>
                                        <SearchableDropdown
                                            options={[
                                                ...[1, 2, 3, 4]
                                                    .map(num => {
                                                        const pref = project.guide?.[`preference${num}`];
                                                        if (!pref) return null;

                                                        const email = getEmail(pref);
                                                        if (!email) return null;

                                                        const guideDetails = guides.find(g => g.email === email);
                                                        const count = guideDetails?.projectCount || 0;
                                                        const name = guideDetails?.name || pref.name || email;

                                                        return {
                                                            label: `Preference ${num}: ${name} - ${count} Projects`,
                                                            value: email,
                                                            _id: `pref_${num}_${email}`,
                                                            isPreference: true
                                                        };
                                                    })
                                                    .filter(Boolean),
                                                ...guides.map(g => ({
                                                    label: `${g.name} (${g.email}) - ${g.projectCount || 0} Projects`,
                                                    value: g.email,
                                                    _id: `guide_${g.email}`,
                                                    isPreference: false
                                                }))
                                            ]}
                                            value={selectedGuide}
                                            onChange={handleGuideSelect}
                                            placeholder="Choose a guide..."
                                            labelKey="label"
                                            valueKey="value"
                                            idKey="_id"
                                            disabledOptions={new Set([project.coGuides?.assigned?.email].filter(Boolean))}
                                            hideOnSearch={(opt) => opt.isPreference}
                                        />
                                        <div className="flex justify-end mt-2">
                                            <button
                                                onClick={() => setShowGuideDropdown(false)}
                                                className="text-xs text-gray-500 hover:text-gray-800 underline"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div id="co-guides" className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" >
                                <div className="flex justify-between items-start mb-4">
                                    <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider flex items-center gap-2">
                                        <FaUserTie /> Co-Guide Prefs
                                    </h3>
                                    {(activeRole === "thrustarea" || activeRole === "pc") && (project.status === "approved" || project.status === "guide-assigned") && (
                                        <button
                                            onClick={() => {
                                                if (!project.guide?.assigned?.email) {
                                                    toast.error("Please assign a Main Guide first.");
                                                    return;
                                                }
                                                handleAssignCoGuide();
                                            }}
                                            className={`text-xs px-3 py-1.5 rounded transition-colors ${!project.guide?.assigned?.email
                                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                                : "bg-[#B7202E] text-white hover:bg-[#961925]"
                                                }`}
                                        >
                                            {project.coGuides?.assigned?.email ? "Reassign" : "Assign"}
                                        </button>
                                    )}
                                </div>

                                <ul className="space-y-3">
                                    {[1, 2, 3, 4].map(num => {
                                        const email = getEmail(project.coGuides?.[`preference${num}`]);
                                        return email ? (
                                            <li key={num} className="flex items-center gap-3 text-sm text-gray-700 p-2 rounded hover:bg-gray-50">
                                                <span className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">{num}</span>
                                                {email}
                                            </li>
                                        ) : null;
                                    })}
                                    {project.coGuides?.assigned?.email && (
                                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                            <div className="text-xs font-bold text-blue-700 uppercase mb-1">Assigned Co-Guide</div>
                                            <div className="font-medium text-blue-900">{project.coGuides.assigned.email}</div>
                                            {project.coGuides.assigned.assignedDate && (
                                                <div className="text-xs text-blue-600 mt-1">
                                                    Assigned on: {new Date(project.coGuides.assigned.assignedDate).toLocaleDateString("en-IN", {
                                                        day: 'numeric', month: 'numeric', year: 'numeric'
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </ul>

                                {showCoGuideDropdown && !isEditing && (
                                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 animate-fade-in">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {project.coGuides?.assigned?.email ? "Reassign Co-Guide" : "Select Co-Guide"}
                                        </label>
                                        <SearchableDropdown
                                            options={[
                                                ...[1, 2, 3, 4]
                                                    .map(num => {
                                                        const pref = project.coGuides?.[`preference${num}`];
                                                        if (!pref) return null;

                                                        const email = getEmail(pref);
                                                        if (!email) return null;

                                                        const guideDetails = guides.find(g => g.email === email);
                                                        const count = guideDetails?.projectCount || 0;
                                                        const name = guideDetails?.name || pref.name || email;

                                                        return {
                                                            label: `Preference ${num}: ${name} - ${count} Projects`,
                                                            value: email,
                                                            _id: `pref_${num}_${email}`,
                                                            isPreference: true
                                                        };
                                                    })
                                                    .filter(Boolean),
                                                ...guides.map(g => ({
                                                    label: `${g.name} (${g.email}) - ${g.projectCount || 0} Projects`,
                                                    value: g.email,
                                                    _id: `guide_${g.email}`,
                                                    isPreference: false
                                                }))
                                            ]}
                                            value={selectedCoGuide}
                                            onChange={handleCoGuideSelect}
                                            placeholder="Choose a co-guide..."
                                            labelKey="label"
                                            valueKey="value"
                                            idKey="_id"
                                            disabledOptions={new Set([project.guide?.assigned?.email].filter(Boolean))}
                                            hideOnSearch={(opt) => opt.isPreference}
                                        />
                                        <div className="flex justify-end mt-2">
                                            <button
                                                onClick={() => setShowCoGuideDropdown(false)}
                                                className="text-xs text-gray-500 hover:text-gray-800 underline"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>


                        <div id="industry-expert" className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-3 flex items-center gap-2">
                                <FaUserTie /> Industry Expert
                            </h3>
                            {isEditing ? (
                                <div className="space-y-4">
                                    <div className="mb-4">
                                        <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">Search and Select Industry Expert</label>
                                        <SearchableDropdown
                                            options={industryExperts}
                                            value={""}
                                            onChange={handleEditIndustryExpertSelect}
                                            placeholder="Search Industry Expert..."
                                            labelKey="name"
                                            valueKey="_id"
                                            subLabelKey="company"
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Name</label>
                                            <input
                                                type="text"
                                                value={editData.industryExpert?.name || ""}
                                                readOnly
                                                className="w-full px-4 py-2 border border-gray-200 bg-gray-100 rounded-lg text-gray-600 cursor-not-allowed focus:outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Email</label>
                                            <input
                                                type="email"
                                                value={editData.industryExpert?.email || ""}
                                                readOnly
                                                className="w-full px-4 py-2 border border-gray-200 bg-gray-100 rounded-lg text-gray-600 cursor-not-allowed focus:outline-none"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Company</label>
                                            <input
                                                type="text"
                                                value={editData.industryExpert?.company || ""}
                                                readOnly
                                                className="w-full px-4 py-2 border border-gray-200 bg-gray-100 rounded-lg text-gray-600 cursor-not-allowed focus:outline-none"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                project.industryExpert && (project.industryExpert.name || project.industryExpert.email || project.industryExpert.company || project.industryExpert.designation) ? (
                                    <div className="space-y-1">
                                        {project.industryExpert.name && (
                                            <div className="p-2 rounded hover:bg-gray-50">
                                                <div className="text-xs font-bold text-gray-500 uppercase mb-1">Name</div>
                                                <div className="text-sm text-gray-700">{project.industryExpert.name}</div>
                                            </div>
                                        )}
                                        {project.industryExpert.email && (
                                            <div className="p-2 rounded hover:bg-gray-50">
                                                <div className="text-xs font-bold text-gray-500 uppercase mb-1">Email</div>
                                                <div className="text-sm text-gray-700">{project.industryExpert.email}</div>
                                            </div>
                                        )}
                                        {project.industryExpert.company && (
                                            <div className="p-2 rounded hover:bg-gray-50">
                                                <div className="text-xs font-bold text-gray-500 uppercase mb-1">Company</div>
                                                <div className="text-sm text-gray-700">{project.industryExpert.company}</div>
                                            </div>
                                        )}
                                        {project.industryExpert.designation && (
                                            <div className="p-2 rounded hover:bg-gray-50">
                                                <div className="text-xs font-bold text-gray-500 uppercase mb-1">Designation</div>
                                                <div className="text-sm text-gray-700">{project.industryExpert.designation}</div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-700">No industry expert assigned.</p>
                                )
                            )}
                        </div>


                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div id="objectives1" className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" >
                                <h3 className="text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2 text-[#B7202E]">
                                    <FaCalendarAlt /> Objectives for 6th sem
                                </h3>
                                {isEditing ? (
                                    <textarea
                                        value={editData.objectivesTerm1 || ""}
                                        onChange={(e) => handleEditChange(e, "objectivesTerm1")}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B7202E] outline-none min-h-[100px]"
                                        placeholder="Enter Objectives for 6th sem which will be completed by the end of 6th sem"
                                    />
                                ) : (
                                    <ReadMore text={project.objectivesTerm1 || "Not specified"} />
                                )}
                            </div>
                            <div id="objectives2" className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" >
                                <h3 className="text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2 text-[#B7202E]">
                                    <FaCalendarAlt /> Objectives for 7th sem
                                </h3>
                                {isEditing ? (
                                    <textarea
                                        value={editData.objectivesTerm2 || ""}
                                        onChange={(e) => handleEditChange(e, "objectivesTerm2")}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B7202E] outline-none min-h-[100px]"
                                        placeholder="Enter Objectives for 7th sem which will be completed by the end of 7th sem"
                                    />
                                ) : (
                                    <ReadMore text={project.objectivesTerm2 || "Not specified"} />
                                )}
                            </div>
                        </div>

                        {/* Thrust Area Section */}

                        <div id="thrust-area" className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" >
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider flex items-center gap-2">
                                    <FaHashtag /> Thrust Area
                                </h3>
                                {(activeRole === 'pc') && (
                                    <button
                                        onClick={handleAssignThrustArea}
                                        className="text-xs bg-[#4caf50] text-white px-3 py-1.5 rounded hover:bg-[#388e3c] transition-colors"
                                    >
                                        {project.thrustArea ? 'Change' : 'Assign'}
                                    </button>
                                )}
                            </div>
                            {project.thrustArea ? (
                                <div className="bg-blue-50 text-blue-800 px-4 py-3 rounded-lg border border-blue-100 inline-block font-medium">
                                    {project.thrustArea}
                                </div>
                            ) : (
                                <div className="text-gray-400 italic">No thrust area assigned yet</div>
                            )}

                            {showThrustAreaDropdown && (
                                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 animate-fade-in">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Select Thrust Area {userDepartment && `(Department: ${userDepartment})`}
                                    </label>
                                    {loadingThrustAreas ? (
                                        <div className="text-sm text-gray-500 italic">Loading thrust areas...</div>
                                    ) : thrustAreas.length > 0 ? (
                                        <select
                                            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#B7202E]"
                                            value={selectedThrustArea}
                                            onChange={handleThrustAreaSelect}
                                        >
                                            <option value="">Choose a thrust area...</option>
                                            {thrustAreas.map((area, idx) => (
                                                <option key={idx} value={area.name}>{area.name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div className="text-sm text-red-500 italic">No thrust areas available for your department.</div>
                                    )}
                                    <button
                                        onClick={() => setShowThrustAreaDropdown(false)}
                                        className="mt-2 text-xs text-gray-500 hover:text-gray-800 underline"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Announcements Section */}
                        <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100" >
                            <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2 pb-4 border-b border-gray-100">
                                <FaBullhorn className="text-[#B7202E]" /> Project Announcements
                            </h3>

                            <div className="space-y-4">
                                {announcements.length > 0 ? (
                                    announcements.map((announcement, index) => (
                                        <div
                                            key={index}
                                            className={`p-4 rounded-xl border transition-all ${announcement.type === 'accepted' ? 'bg-green-50 border-green-100' :
                                                announcement.type === 'rejected' ? 'bg-red-50 border-red-100' :
                                                    announcement.type === 'modifications' ? 'bg-blue-50 border-blue-100' :
                                                        'bg-gray-50 border-gray-100'
                                                }`}
                                        >
                                            <div className="flex justify-between items-start mb-2">
                                                <div className={`font-bold text-sm uppercase tracking-wide ${announcement.type === 'accepted' ? 'text-green-700' :
                                                    announcement.type === 'rejected' ? 'text-red-700' :
                                                        announcement.type === 'modifications' ? 'text-blue-700' :
                                                            'text-gray-700'
                                                    }`}>
                                                    {announcement.title || announcement.type}
                                                </div>
                                                <span className="text-xs text-gray-400 bg-white px-2 py-1 rounded-full border border-gray-100 shadow-sm">
                                                    {new Date(announcement.date).toLocaleDateString('en-IN', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    })}
                                                </span>
                                            </div>
                                            <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                                                {announcement.message}
                                            </p>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-12 text-gray-400 italic bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                        No announcements found for this project
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            {/* Action Modals */}
            {(approved || rejected || modifications) && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all scale-100">
                        <div className={`p-4 text-white font-bold text-lg flex justify-between items-center ${approved ? 'bg-green-600' : rejected ? 'bg-red-600' : 'bg-blue-600'}`}>
                            <span>
                                {approved ? "Approve Project" : rejected ? "Reject Project" : "Request Modifications"}
                            </span>
                            <button onClick={handleCancel} className="text-white/80 hover:text-white transition-colors">
                                <FaTimes />
                            </button>
                        </div>

                        <div className="p-6">
                            <p className="text-gray-600 mb-4 text-sm">
                                {approved
                                    ? "Are you sure you want to approve this project? You can add an optional message."
                                    : rejected
                                        ? "Please provide a reason for rejecting this project."
                                        : "Please specify the modifications required for this project."}
                            </p>

                            <textarea
                                className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 focus:outline-none resize-none text-sm mb-6 transition-all"
                                style={{
                                    borderColor: approved ? '#16a34a' : rejected ? '#dc2626' : '#2563eb',
                                    '--tw-ring-color': approved ? '#16a34a' : rejected ? '#dc2626' : '#2563eb'
                                }}
                                placeholder={approved ? "Optional approval message..." : "Enter your message here..."}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                            ></textarea>

                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={handleCancel}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSend}
                                    className={`px-6 py-2 text-white rounded-lg text-sm font-bold shadow-sm hover:shadow-md transition-all transform active:scale-95 ${approved ? 'bg-green-600 hover:bg-green-700' : rejected ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

}

export default ProjectDetailsContainer;
